// 📄 pages/Proyectos/components/ReporteExtension.tsx
// Reporte que adjunta la extensión de Chrome a un ticket de la Mesa de ayuda
// (D3 de plan-diagnostico-documentos.md, T6 de plan-tickets-proyecto.md).
import React from 'react';
import { ExternalLink, BookPlus } from 'lucide-react';
import type { TicketReporte } from '../../../services/mesaAyudaService.ts';
import { ESTATUS_DOCUMENTO_BN, nombreDocumentoReporte } from '../../../services/knownErrorsService.ts';
import type { KnownErrorPrefill } from '../../Admin/components/KnownErrorFormModal.tsx';

// "Convertir en error conocido": el evento más representativo de la falla
// (el primero con código y palabras de error; si no, el primero con código)
// propone el código, el título y el primer patrón. La causa y la solución
// salen de la explicación de la IA, si la hubo. El admin revisa todo antes
// de guardar (en especial el TEXTO del patrón, que queda vacío a propósito: si
// no lo llena, el formulario avisa antes de guardar).
const RE_FALLA = /fail|error|reject|not found|invalid|denied|rechaz/i;
export const prefillDesdeReporte = (reporte: TicketReporte): KnownErrorPrefill => {
  const eventos = reporte.eventos || [];
  const evento = eventos.find((e) => e.codigo && RE_FALLA.test(e.texto)) || eventos.find((e) => e.codigo) || eventos[0];
  const estatus = reporte.documento?.estatusNormalizado;
  return {
    module: 'ariba',
    code: evento?.codigo || '',
    title: (evento?.texto || '').slice(0, 120),
    cause: reporte.explicacionIa?.porQue || '',
    solution: reporte.explicacionIa?.queHacer || '',
    tags: [reporte.plataforma === 'CIG' ? 'cig' : 'business-network'],
    tiposDocumento: reporte.documento?.tipo ? [reporte.documento.tipo] : [],
    estatusAplica: ESTATUS_DOCUMENTO_BN.some((e) => e.id === estatus) ? [estatus] : [],
    patrones: evento?.codigo ? [{ codigo: evento.codigo, texto: '' }] : []
  };
};

/**
 * Reporte que adjuntó la extensión de Chrome al ticket (D3). También lo usa
 * la Mesa de ayuda del proyecto; "Convertir en error conocido" solo aparece
 * si se pasa `onConvertir` (admins de la plataforma: el catálogo es global).
 */
export function ReporteExtension({ reporte, onConvertir }: { reporte: TicketReporte; onConvertir?: () => void }) {
  const { documento, coincidencias = [], explicacionIa, eventos = [] } = reporte;
  const urlSegura = reporte.urlPagina && /^https:\/\//i.test(reporte.urlPagina) ? reporte.urlPagina : null;
  const esCig = reporte.plataforma === 'CIG';
  const sitio = esCig ? 'Managed Gateway (CIG)' : 'Business Network';
  return (
    <div className="mt-2 bg-black/20 border border-white/10 rounded-lg p-3 space-y-2 text-xs text-gray-300">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-medium text-white">Reporte de la extensión · {sitio}</p>
        <div className="flex items-center gap-3">
          {urlSegura && (
            <a href={urlSegura} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:text-blue-300">
              <ExternalLink className="w-3.5 h-3.5" /> Abrir en {sitio}
            </a>
          )}
          {onConvertir && (
            <button type="button" onClick={onConvertir} className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300">
              <BookPlus className="w-3.5 h-3.5" /> Convertir en error conocido
            </button>
          )}
        </div>
      </div>
      <p>
        <span className="font-mono">{nombreDocumentoReporte(documento)}</span> · {documento?.estatus || 'sin estatus'} · ambiente {reporte.ambiente}
        {reporte.buyerAnid ? <> · ANID <span className="font-mono">{reporte.buyerAnid}</span></> : null}
        {reporte.clienteId ? <> · cliente {reporte.clienteId}</> : ' · cliente sin resolver'}
      </p>
      <p>
        <span className="text-gray-500">Errores conocidos que coinciden: </span>
        {coincidencias.length ? coincidencias.map((c) => `${c.code} (${c.title})`).join(', ') : 'ninguno'}
      </p>
      {explicacionIa && (
        <div className="space-y-1">
          <p className="text-gray-500">Explicación de la IA{explicacionIa.confianza ? ` (confianza ${explicacionIa.confianza})` : ''}:</p>
          {explicacionIa.quePaso && <p className="whitespace-pre-wrap"><span className="text-gray-500">Qué pasó: </span>{explicacionIa.quePaso}</p>}
          {explicacionIa.porQue && <p className="whitespace-pre-wrap"><span className="text-gray-500">Por qué: </span>{explicacionIa.porQue}</p>}
          {explicacionIa.queHacer && <p className="whitespace-pre-wrap"><span className="text-gray-500">Qué hacer: </span>{explicacionIa.queHacer}</p>}
        </div>
      )}
      <details>
        <summary className="cursor-pointer text-gray-400 hover:text-white">
          {esCig ? 'Mensaje de error de CIG' : 'Eventos del historial'} ({eventos.length}{reporte.eventosTotales ? `, de ${reporte.eventosTotales} filas` : ''})
        </summary>
        <ul className="mt-1 space-y-1 list-disc pl-4 break-words">
          {eventos.map((e, i) => (
            <li key={i}>
              {e.codigo && <span className="font-mono text-amber-300">{e.codigo}: </span>}
              {e.texto}
              {e.fecha && <span className="text-gray-500"> · {e.fecha}</span>}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
